import { TerminalView } from '../view/TerminalView';
import { templateTerminal } from '../view/template';
import { TerminalRepository } from '../repository/TerminalRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { TerminalWebComponent } from '..';

import {ElementClass, elementClassListAdd, elementClassListRemove, isHidden, toggleVisibility} from '../../../utils/htmlElement.utils';

import { NirvanaError } from '@nirvana/shared/error';
import { InputEvents } from '../../../ui/gzInput/types';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { EndpointDetailsWebComponentSchema } from '../../../endpointDetails/types';
import {
  CONNECTION_ENDED,
  CONNECTION_ERROR,
  CONNECTION_FAILED,
  CONNECTION_INACTIVE,
  CONNECTION_SUCCESSFUL,
  DownloadedFile,
  DownloadFinishedErrorCodes,
  START_CONNECTION,
  UploadedFile,
  UploadFinishedErrorCodes,
  UploadFinishedStatus,
} from '../types';

import {
  DEFAULT_SIZE,
  NirvanaErrorCode,
  StreamMessageType,
  StreamStopReason,
} from '@nirvana/shared/dtos/types/shared/communication';
import { v4 as uuidv4 } from 'uuid';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import moment from 'moment-timezone';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastActionButtonMode, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { User } from '../../../company/types/user';
import { FormEvent } from '../../../ui/gzForm/formField/types/form-event.enum';
import { Source } from '../constants';
import { translateFn } from '../../../utils/translator.helper';
import { ServiceMessageFromEndpoint } from '@nirvana/shared/dtos/types/remote-shell';
import { getFilenameFromPath } from '../utils';
import _ from 'lodash';

export class TerminalController extends BaseWebComponentController {
  public component: TerminalWebComponent;
  public dataRepository: TerminalRepository;
  public view: TerminalView;
  public viewConstructor: typeof TerminalView;
  public dataRepositoryConstructor: typeof TerminalRepository;

  protected xterm: {
    instance: Terminal | null;
    fitAddon: FitAddon | null;
    input: any;
    output: any;
    streamId: string;
  } = {
    instance: null,
    fitAddon: null,
    input: null,
    output: null,
    streamId: '',
  };

  protected windowResizeListener: any;

  protected isIdpRequestAuthorized: boolean;
  protected isMdrAuthorized: boolean;

  protected sessionTimers: {
    start: Date | null;
    end: Date | null;
  } = {
    start: null,
    end: null,
  };

  protected resizeTimeout;

  constructor(component: TerminalWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: TerminalView,
      viewTemplate: templateTerminal,
      dataRepositoryConstructor: TerminalRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$authenticationCodeInput,
        event: FormEvent.ValueChange,
        callback: this.handleAuthenticationValueChanged,
      },
      {
        element: this.view.dom.$authenticationCodeInput.Input,
        event: InputEvents.BLUR,
        callback: this.handleAuthenticationValueChanged,
      },
      {
        element: this.view.dom.$startSessionButton,
        event: ButtonEvents.CLICK,
        callback: this.handleStartSessionButtonClick,
      },
      {
        element: this.view.dom.$restartSessionButton,
        event: ButtonEvents.CLICK,
        callback: this.handleRestartSessionButtonClick,
      },
      {
        element: this.view.dom.$downloadArchiveButton,
        event: ButtonEvents.CLICK,
        callback: this.handleDownloadArchiveButton,
      },
    ];
  }

  async getDataForView(): Promise<any> {
    const data = await this.dataRepository.getDataForView();
    this.isIdpRequestAuthorized = data.isIdpRequestAuthorized;
    this.isMdrAuthorized = data.isMdrGracePeriod;
    if (this.isIdpRequestAuthorized) {
      await this.handleStartSessionButtonClick();
      this.isIdpRequestAuthorized = false;
    } else if (data.isMdrGracePeriod) {
      await this.handleStartSessionButtonClick();
    }
    return data;
  }

  handleDisconnect(): void {
    this.closeTerminal();
  }

  async handleDataLoaded(): Promise<void> {
    await this.setStartButtonState();
  }

  protected async handleAuthenticationValueChanged(): Promise<void> {
    await this.setStartButtonState();
  }

  protected async setStartButtonState() {
    let disabled = false;
    let tooltip = '';

    const { errors } = this.view.getTranslations();

    do {
      if (!this.component.endpoint) {
        tooltip = errors.endpointNotFound;
        disabled = true;
        break;
      }

      const user = this.dependencies.user;

      if (!disabled) {
        if (!this.dependencies.userHasRemoteAccessFeature()) {
          tooltip = errors.remoteAccessFeatureNotAvailable;
          disabled = true;
          break;
        }
      }

      if (!disabled) {
        ({ tooltip, disabled } = this.checkConditionsForEndpoint(this.component.endpoint));
      }

      if (!disabled) {
        if (this.isSamlUser(user)) {
          ({ tooltip, disabled } = await this.checkConditionsForSAMLUser(user));
        } else {
          ({ tooltip, disabled } = this.checkConditionsForUser(user));
        }
      }
    } while (false);

    this.view.dom.$startSessionButton.disabled = await this.getStartSessionButtonStateForUser(
      this.dependencies.user,
      disabled
    );
    this.view.dom.$startSessionButtonTooltip.tooltipText = tooltip;
  }

  async getStartSessionButtonStateForUser(user, state) {
    if (this.isSamlUser(user)) {
      return state;
    }
    return state || !(await this.view.dom.$authenticationCodeInput.dryValidate());
  }

  protected checkConditionsForEndpoint(endpoint: EndpointDetailsWebComponentSchema): {
    tooltip: string;
    disabled: boolean;
  } {
    const {
      isManaged,
      features: { remoteAccess },
      ma,
    } = endpoint;

    const { errors } = this.view.getTranslations();

    if (!isManaged) {
      return {
        tooltip: errors.unmanagedEndpoint,
        disabled: true,
      };
    }

    // remote shell on endpoint
    if (!remoteAccess) {
      return {
        tooltip: errors.moduleNotInstalled,
        disabled: true,
      };
    }

    if (!ma.company || !ma.endpoint) {
      return {
        tooltip: errors.maNotEnabled,
        disabled: true,
      };
    }

    return {
      tooltip: '',
      disabled: false,
    };
  }

  protected checkConditionsForUser(user: User): { tooltip: string; disabled: boolean } {
    const { errors } = this.view.getTranslations();

    // 2FA
    if (!user.twoFactorAuthentication || !user.twoFactorAuthentication.enabled) {
      return {
        tooltip: errors.twoFactorAuthenticationDisabled,
        disabled: true,
      };
    }

    // manage inventory + manage remote shell
    if (!user.role.rights['manageInventory'] || !user.role.rights['manageRemoteShell']) {
      return {
        tooltip: errors.notEnoughUserRights,
        disabled: true,
      };
    }

    return {
      tooltip: '',
      disabled: false,
    };
  }

  async checkConditionsForSAMLUser(user: User) {
    const { errors } = this.view.getTranslations();

    const authenticationSettings = this.component.authenticationSettings;

    const idp = authenticationSettings.data.specifics?.identityProvider?.specifics?.metadata;
    if (!idp) {
      return {
        tooltip: errors.missingIDP,
        disabled: true,
      };
    }
    if (!user.role.rights['manageInventory'] || !user.role.rights['manageRemoteShell']) {
      return {
        tooltip: errors.notEnoughUserRights,
        disabled: true,
      };
    }

    return {
      tooltip: '',
      disabled: false,
    };
  }

  isSamlUser(user: User) {
    return user.source === Source.SOURCE_SSO_SAML;
  }

  public async handleStartSessionButtonClick(): Promise<void> {
    if (this.view.dom.$startSessionButton.showSpinner) {
      // do nothing if the connection is trying to start
      return;
    }
    let authCode = '';
    if (this.isSamlUser(this.dependencies.user)) {
      if (!this.isIdpRequestAuthorized) {
        await this.redirectToIdp();
        return;
      }
    } else {
      authCode = this.view.dom.$authenticationCodeInput.Input.getFormValue();
    }

    this.view.dom.$startSessionButton.showSpinner = true;

    this.dispatchEvent(START_CONNECTION);

    this.initializeTerminal();

    const size = this.xterm.fitAddon?.proposeDimensions() || DEFAULT_SIZE;

    try {
      const { input, output, streamId } = await this.dependencies.api.clients.RemoteShell.connectToEndpoint(
        this.dependencies.endpointId,
        authCode,
        {
          size,
        }
      );

      this.xterm.streamId = streamId;
      this.handleSuccessfulConnection(input, output, streamId);
    } catch (error) {
      await this.handleStartConnectionError(error);
    }

    this.view.dom.$startSessionButton.showSpinner = false;
  }

  async redirectToIdp() {
    const { data } = this.component.authenticationSettings;
    const idp = data.specifics.identityProvider.specifics.metadata;
    const email = this.dependencies.user.email;
    const redirectUrlParams = '#!/remote-shell/' + this.component.endpoint?.endpointId;
    let url = Application.Main.getSsoDomain() + '/sp/auth?';
    url += [
      'idp=' + encodeURIComponent(idp),
      'zone=' + encodeURIComponent(this.getZoneUrl()),
      'email=' + encodeURIComponent(email),
      'forceAuth=' + encodeURIComponent(1),
      'redirectUrlParams=' + encodeURIComponent(redirectUrlParams),
      'id=' + encodeURIComponent(uuidv4()),
    ].join('&');

    window.location.replace(url);
  }

  protected getZoneUrl() {
    return window.location.protocol + '//' + window.location.hostname + '/';
  }

  protected async handleStartConnectionError(error: NirvanaError): Promise<void> {
    const { code } = error;

    if (code === NirvanaErrorCode.TWO_FACTOR_AUTHENTICATION_FAILED) {
      if (isHidden(this.view.dom.$authenticationCodeInput)) {
        toggleVisibility(this.view.dom.$authenticationCodeInput, true);
        toggleVisibility(this.view.dom.$startSessionInfoSecondLine, true);
      }

      this.view.dom.$authenticationCodeInput.markAsInvalid(
        this.view.translateNirvanaErrorCode(NirvanaErrorCode.TWO_FACTOR_AUTHENTICATION_FAILED)
      );

      await this.handleAuthenticationFailed();
      return;
    }

    if (code === NirvanaErrorCode.IDP_AUTH_FAILED) {
      await this.handleAuthenticationFailed();
      return;
    }

    await this.handleFailedConnection(error);
  }

  protected async handleAuthenticationFailed() {
    await this.showStartSession();
    this.dispatchEvent(CONNECTION_FAILED);
    this.resetXTerm();
  }

  protected handleSuccessfulConnection(input, output, streamId: string) {
    this.resetAuthenticationCodeInput();

    if (this.dependencies.user.mdrSuperAnalyst) {
      this.isMdrAuthorized = true;
    }

    this.addTerminalHandlers(input, output);

    this.dispatchEvent(CONNECTION_SUCCESSFUL, { streamId, xterm: this.xterm });

    this.setStartSessionTime();
  }

  protected async handleFailedConnection(error: NirvanaError | null): Promise<void> {
    this.resetAuthenticationCodeInput();
    await this.showStartSession();

    this.dispatchEvent(CONNECTION_FAILED, { error });
  }

  protected resetAuthenticationCodeInput(): void {
    this.view.dom.$authenticationCodeInput.Input.setFormValue('');
    this.view.dom.$authenticationCodeInput.resetField();
    this.view.dom.$startSessionButton.disabled = true;
  }

  protected initializeTerminal(): void {
    this.showTerminal();

    this.xterm.instance = new Terminal();
    this.xterm.instance.setOption('fontSize', 14); // fontSize need to be an even number otherwise some characters are not shown properly
    this.xterm.fitAddon = new FitAddon();
    this.xterm.instance.loadAddon(this.xterm.fitAddon);
    this.xterm.instance.open(this.view.dom.$terminalContainer);
  }

  protected addTerminalHandlers(input, output): void {
    const { instance } = this.xterm;

    this.xterm.fitAddon?.fit();
    instance?.onData(e => {
      // batches of maximum 50k bytes, because encoding will add another 16k and we need to maintain the message size under 100k
      const batches = e.match(/.{1,50000}/gs);
      if (!batches) {
        return;
      }

      for (const batch of batches) {
        input.next({
          type: StreamMessageType.DATA,
          data: {
            encodedBuffer: btoa(batch),
          },
          id: uuidv4(),
        });
      }
    });

    output.asObservable().subscribe({
      next: this.handleStreamMessage.bind(this),
      error: error => {
        this.dispatchEvent(CONNECTION_ERROR, { error });
        this.closeTerminal();
      },
    });

    this.xterm = {
      ...this.xterm,
      input,
      output,
    };

    this.windowResizeListener = this.handleWindowResize.bind(this);
    window.addEventListener('resize', this.windowResizeListener);
  }

  protected handleStreamMessage(message): void {
    switch (message.type) {
      case StreamMessageType.DATA:
        const { data } = message;
        this.xterm.instance?.write(data.message);

        break;
      case StreamMessageType.RESIZE:
        const { resize } = message;
        this.xterm.instance?.resize(resize.cols, resize.rows);
        break;

      case StreamMessageType.STREAM_STOP:
        const { streamStop } = message;
        const { reason } = streamStop;

        if (reason === StreamStopReason.INACTIVITY) {
          this.dispatchEvent(CONNECTION_INACTIVE);
        }

        this.resetXTerm();
        this.showEndSession();
        break;

      case ServiceMessageFromEndpoint.TYPE.DOWNLOAD_FINISHED:
        this.handleDownloadFinished(message);
        break;

      case ServiceMessageFromEndpoint.TYPE.UPLOAD_FINISHED:
        this.handleUploadFinished(message);
        break;

      default:
        console.error('unknown message type', message);
    }
  }

  protected handleWindowResize(): void {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      const { cols, rows } = this.xterm.fitAddon?.proposeDimensions() || DEFAULT_SIZE;

      this.xterm.input.next({
        type: StreamMessageType.RESIZE,
        resize: {
          cols,
          rows,
        },
        id: uuidv4(),
      });
    }, 250); // debounce
  }

  public sendMessage(message) {
    this.xterm.input.next(message);
  }

  protected closeTerminal(): void {
    window.removeEventListener('resize', this.windowResizeListener);
    this.windowResizeListener = null;

    this.xterm.input.complete();
    this.xterm.output.complete();

    this.resetXTerm();

    this.showEndSession();
  }

  protected resetXTerm() {
    this.xterm.instance?.dispose();
    this.xterm.input = null;
    this.xterm.output = null;
    this.xterm.instance = null;
    this.xterm.fitAddon = null;
  }

  protected dispatchEvent(eventName, detail = {}): void {
    this.view.dom.$wrapper.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  protected async handleRestartSessionButtonClick(): Promise<void> {
    if (this.isSamlUser(this.dependencies.user)) {
      let response = await this.dataRepository.isIdpRequestAuthorized(this.dependencies.endpointId);

      if (typeof response !== 'boolean') {
        response = false;
      }

      if (!response) {
        await this.redirectToIdp();
      }
      return;
    }

    await this.showStartSession();

    if (this.isMdrAuthorized) {
      this.view.dom.$startSessionButton.disabled = false;
      toggleVisibility(this.view.dom.$authenticationCodeInput, false);
      toggleVisibility(this.view.dom.$startSessionInfoSecondLine, false);
    }
  }

  protected async handleDownloadArchiveButton(): Promise<void> {
    if (this.view.dom.$downloadArchiveButton.showSpinner) {
      return;
    }

    this.view.dom.$downloadArchiveButton.showSpinner = true;
    try {
      const response = await this.dataRepository.checkFileExists(this.xterm.streamId);
      if (response) {
        this.dependencies.downloadArchive(this.xterm.streamId);
        this.view.dom.$downloadArchiveButton.showSpinner = false;
        return;
      }
    } catch {}

    this.view.dom.$downloadArchiveButton.showSpinner = false;

    GzToastNotificationWebComponent.buildToastWrapperElement(
      {
        message: this.view.getTranslations().archiveNotAvailable,
        severity: ToastSeverity.INFORMATIVE,
      },
      undefined,
      false
    );
  }

  protected async showStartSession(): Promise<void> {
    this.resetSessionTime();
    this.resetXTerm();

    if (this.isSamlUser(this.dependencies.user)) {
      await this.setStartButtonState();
    } else {
      // First time, the auth code will not be populated.
      // After incorrect auth code, user must be forced to change the bad auth code, so he cannot try to start a new session with same code
      this.view.dom.$startSessionButton.disabled = true;
    }

    elementClassListRemove(this.view.dom.$startSessionContainer, ElementClass.HIDDEN);
    elementClassListAdd(this.view.dom.$terminalContainer, ElementClass.HIDDEN);
    elementClassListAdd(this.view.dom.$endSessionContainer, ElementClass.HIDDEN);
    GzToastNotificationWebComponent.closeAllToasts();
  }

  protected showTerminal(): void {
    elementClassListAdd(this.view.dom.$startSessionContainer, ElementClass.HIDDEN);
    elementClassListRemove(this.view.dom.$terminalContainer, ElementClass.HIDDEN);
    elementClassListAdd(this.view.dom.$endSessionContainer, ElementClass.HIDDEN);
    GzToastNotificationWebComponent.closeAllToasts();
  }

  protected showEndSession(): void {
    this.setEndSessionTime();
    elementClassListAdd(this.view.dom.$startSessionContainer, ElementClass.HIDDEN);
    elementClassListAdd(this.view.dom.$terminalContainer, ElementClass.HIDDEN);
    elementClassListRemove(this.view.dom.$endSessionContainer, ElementClass.HIDDEN);
    this.dispatchEvent(CONNECTION_ENDED);
  }

  protected setStartSessionTime(): void {
    this.view.dom.$sessionStartTimeContent.textContent = this.getFormattedNowDate();
  }

  protected setEndSessionTime(): void {
    this.view.dom.$sessionEndTimeContent.textContent = this.getFormattedNowDate();
  }

  protected resetSessionTime(): void {
    this.view.dom.$sessionStartTimeContent.textContent = '';
    this.view.dom.$sessionEndTimeContent.textContent = '';
  }

  protected getFormattedNowDate(): string {
    return moment().format('DD MMMM YYYY, HH:mm:ss');
  }

  protected handleUploadFinished(message) {
    const { remoteAccessUploadFinished } = message;

    const { downloadErrors } = this.view.getTranslations();

    GzToastNotificationWebComponent.closeAllToasts();

    switch (remoteAccessUploadFinished.status) {
      case UploadFinishedStatus.SUCCESS:
        this.handleUploadSuccessResponse(remoteAccessUploadFinished, downloadErrors);
        return;

      case UploadFinishedStatus.GATHER_ITEMS_FAILED:
        this.handleUploadFailedResponse(remoteAccessUploadFinished, downloadErrors);
        return;

      case UploadFinishedStatus.UPLOAD_FAILED:
        const centralizedStorageErrorCode = _.get(remoteAccessUploadFinished, 'centralizedStorage.error', null);

        if (centralizedStorageErrorCode === null) {
          this.createErrorToastMessage(
            downloadErrors.defaultInternalError(UploadFinishedErrorCodes.INTERNAL_ERROR_WEBCOMPONENTS)
          );
          return;
        }

        const toastMessage = this.getCentralizedStorageErrorMessageForUploadFinishedEvent(
          centralizedStorageErrorCode,
          downloadErrors
        );
        this.createErrorToastMessage(toastMessage);

        return;
      case UploadFinishedStatus.ABORTED:
        this.createErrorToastMessage(downloadErrors.centralizedStorageErrors.uploadCancelled);
        return;
      default:
        this.createErrorToastMessage(
          translateFn('Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.', {
            ERROR_CODE: UploadFinishedErrorCodes.INTERNAL_ERROR_WEBCOMPONENTS,
          })
        );
        return;
    }
  }

  getErrorMessageForUploadFinishedEvent(errorCode, files, downloadErrors) {
    errorCode = parseInt(errorCode, 10);
    if (files.length === 1) {
      return this.getSingleFileErrorMessageForUploadFinishedEvent(errorCode, files[0].pattern, downloadErrors);
    }

    return this.getMultipleFilesErrorMessageForUploadFinishedEvent(errorCode, files.length, downloadErrors);
  }

  protected createErrorToastMessage(toastMessage) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: toastMessage,
      severity: ToastSeverity.ERROR,
      keepOpen: true,
    });
  }

  /**
   *
   * @param remoteAccessUploadFinished
   * @param downloadErrors
   * @protected
   */
  protected handleUploadSuccessResponse(remoteAccessUploadFinished, downloadErrors) {
    const files = _.get(remoteAccessUploadFinished, 'centralizedStorage.httpResponse.body.files', null);

    if (!files || !Array.isArray(files)) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: downloadErrors.defaultInternalError(UploadFinishedErrorCodes.INTERNAL_ERROR_WEBCOMPONENTS),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
      return;
    }

    const archiveFile = files.shift();

    if (!archiveFile.id) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: downloadErrors.defaultInternalError(UploadFinishedErrorCodes.INTERNAL_ERROR_WEBCOMPONENTS),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
      return;
    }

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: translateFn('The archive is ready to download.'),
      severity: ToastSeverity.SUCCESS,
      keepOpen: true,
      hasAction: true,
      actionButtonMode: ToastActionButtonMode.BUTTON,
      actionButtonText: translateFn('Download'),
      actionCallback: () => {
        Application.lib.DownloadManager.download(`/storage/remoteAccess/${archiveFile.id}`);
        GzToastNotificationWebComponent.closeAllToasts();
      },
    });

    const items = remoteAccessUploadFinished.items;

    if (!items) {
      return;
    }

    const filesGroupedByError = {} as { [key: number]: Array<DownloadedFile> };

    for (const file of items) {
      const { errorCode } = file;

      if (errorCode === UploadFinishedErrorCodes.SUCCESS) {
        continue;
      }

      if (!filesGroupedByError[errorCode]) {
        filesGroupedByError[errorCode] = [];
      }

      filesGroupedByError[errorCode].push(file);
    }

    for (const [errorCode, files] of Object.entries<Array<DownloadedFile>>(filesGroupedByError)) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.getErrorMessageForUploadFinishedEvent(errorCode, files, downloadErrors),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
    }
  }

  protected handleUploadFailedResponse(remoteAccessUploadFinished, downloadErrors) {
    const items = remoteAccessUploadFinished.items;

    if (!items) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: downloadErrors.defaultInternalError(UploadFinishedErrorCodes.INTERNAL_ERROR_WEBCOMPONENTS),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
      return;
    }

    const filesGroupedByError = {} as { [key: number]: Array<DownloadedFile> };

    for (const file of items) {
      const { errorCode } = file;

      if (!filesGroupedByError[errorCode]) {
        filesGroupedByError[errorCode] = [];
      }

      filesGroupedByError[errorCode].push(file);
    }

    for (const [errorCode, files] of Object.entries<Array<DownloadedFile>>(filesGroupedByError)) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.getErrorMessageForUploadFinishedEvent(errorCode, files, downloadErrors),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
    }
  }

  /**
   *
   * @param archiveErrorCode
   * @param downloadErrors
   * @returns {any}
   * @protected
   */
  protected getCentralizedStorageErrorMessageForUploadFinishedEvent(archiveErrorCode, downloadErrors) {
    switch (archiveErrorCode) {
      case UploadFinishedErrorCodes.MAX_SIZE_EXCEEDED:
        return downloadErrors.centralizedStorageErrors.maxSizeExceeded(this.component.maxFilesSize);
      case UploadFinishedErrorCodes.UPLOAD_CANCELLED:
        return downloadErrors.centralizedStorageErrors.uploadCancelled;
      case UploadFinishedErrorCodes.INVALID_PROFILE:
      case UploadFinishedErrorCodes.TIMEOUT_REACHED:
      case UploadFinishedErrorCodes.INVALID_SERVER:
      case UploadFinishedErrorCodes.INVALID_PROXY:
      case UploadFinishedErrorCodes.COULD_NOT_CONNECT:
      case UploadFinishedErrorCodes.COULD_NOT_LOGIN:
        return downloadErrors.defaultInternalError(archiveErrorCode);

      default:
        return translateFn('Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.', {
          ERROR_CODE: archiveErrorCode,
        });
    }
  }

  /**
   *
   * @param archiveErrorCode
   * @param filePattern
   * @param downloadErrors
   * @returns {any}
   * @protected
   */
  protected getSingleFileErrorMessageForUploadFinishedEvent(archiveErrorCode, filePattern, downloadErrors) {
    const fileName = getFilenameFromPath(filePattern);

    switch (archiveErrorCode) {
      case UploadFinishedErrorCodes.INTERNAL_ERROR:
        return downloadErrors.singleFileErrors.internalError(fileName, archiveErrorCode);
      case UploadFinishedErrorCodes.NOT_PROCESSED:
        return downloadErrors.singleFileErrors.notProcessed(fileName, archiveErrorCode);
      case UploadFinishedErrorCodes.INVALID_NAME:
        return downloadErrors.singleFileErrors.invalidName(fileName);
      case UploadFinishedErrorCodes.ERROR_BAD_PATHNAME:
        return downloadErrors.singleFileErrors.badPathname(fileName);
      case UploadFinishedErrorCodes.RELATIVE_PATH:
        return downloadErrors.singleFileErrors.relativePath(fileName);
      case UploadFinishedErrorCodes.PATH_NOT_FOUND:
        return downloadErrors.singleFileErrors.pathNotFound(fileName);
      case UploadFinishedErrorCodes.EMPTY_FILES:
        return downloadErrors.singleFileErrors.emptyFiles(fileName);
      case UploadFinishedErrorCodes.ACCESS_DENIED:
        return downloadErrors.singleFileErrors.accessDenied(fileName);
      case UploadFinishedErrorCodes.INCOMPLETE_PACKING:
        return downloadErrors.singleFileErrors.incompletePacking(fileName);
      case UploadFinishedErrorCodes.PACKING_FAILED:
        return downloadErrors.singleFileErrors.packingFailed(fileName);

      default:
        return translateFn(
          '{FILE_NAME} could not be downloaded. Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
          {
            FILE_NAME: fileName,
            ERROR_CODE: archiveErrorCode,
          }
        );
    }
  }

  /**
   *
   * @param archiveErrorCode
   * @param numberOfFiles
   * @param downloadErrors
   * @returns {any}
   * @protected
   */
  protected getMultipleFilesErrorMessageForUploadFinishedEvent(archiveErrorCode, numberOfFiles, downloadErrors) {
    switch (archiveErrorCode) {
      case UploadFinishedErrorCodes.INTERNAL_ERROR:
        return downloadErrors.multipleFilesErrors.internalError(numberOfFiles, archiveErrorCode);
      case UploadFinishedErrorCodes.NOT_PROCESSED:
        return downloadErrors.multipleFilesErrors.notProcessed(numberOfFiles, archiveErrorCode);
      case UploadFinishedErrorCodes.INVALID_NAME:
        return downloadErrors.multipleFilesErrors.invalidName(numberOfFiles);
      case UploadFinishedErrorCodes.ERROR_BAD_PATHNAME:
        return downloadErrors.multipleFilesErrors.badPathname(numberOfFiles);
      case UploadFinishedErrorCodes.RELATIVE_PATH:
        return downloadErrors.multipleFilesErrors.relativePath(numberOfFiles);
      case UploadFinishedErrorCodes.PATH_NOT_FOUND:
        return downloadErrors.multipleFilesErrors.pathNotFound(numberOfFiles);
      case UploadFinishedErrorCodes.EMPTY_FILES:
        return downloadErrors.multipleFilesErrors.emptyFiles(numberOfFiles);
      case UploadFinishedErrorCodes.ACCESS_DENIED:
        return downloadErrors.multipleFilesErrors.accessDenied(numberOfFiles);
      case UploadFinishedErrorCodes.INCOMPLETE_PACKING:
        return downloadErrors.multipleFilesErrors.incompletePacking(numberOfFiles);
      case UploadFinishedErrorCodes.PACKING_FAILED:
        return downloadErrors.multipleFilesErrors.packingFailed(numberOfFiles);

      default:
        return translateFn(
          '{NUMBER_OF_FILES} files could not be downloaded. Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
          {
            NUMBER_OF_FILES: numberOfFiles,
            ERROR_CODE: archiveErrorCode,
          }
        );
    }
  }

  protected handleDownloadFinished(message) {
    const { remoteAccessDownloadFinished } = message;

    const items = remoteAccessDownloadFinished.items;
    const filesGroupedByError = {} as { [key: number]: Array<UploadedFile> };

    const successfulFiles: Array<UploadedFile> = [];
    for (const file of items) {
      const { errorCode } = file;

      if (errorCode === DownloadFinishedErrorCodes.SUCCESS) {
        successfulFiles.push(file);
        continue;
      }

      if (!filesGroupedByError[errorCode]) {
        filesGroupedByError[errorCode] = [];
      }

      filesGroupedByError[errorCode].push(file);
    }

    GzToastNotificationWebComponent.closeAllToasts();

    if (successfulFiles.length) {
      if (successfulFiles.length === 1) {
        GzToastNotificationWebComponent.buildToastWrapperElement({
          message: translateFn('{NAME_OF_FILE} successfully uploaded.', {
            NAME_OF_FILE: getFilenameFromPath(successfulFiles[0].to),
          }),
          severity: ToastSeverity.SUCCESS,
        });
      } else {
        GzToastNotificationWebComponent.buildToastWrapperElement({
          message: translateFn('{NUMBER_OF_FILES} files successfully uploaded.', {
            NUMBER_OF_FILES: successfulFiles.length,
          }),
          severity: ToastSeverity.SUCCESS,
        });
      }
    }

    for (const [errorCode, files] of Object.entries<Array<UploadedFile>>(filesGroupedByError)) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.getErrorMessageForDownloadFinishedEvent(errorCode, files),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
    }
  }

  getErrorMessageForDownloadFinishedEvent(errorCode, files) {
    errorCode = parseInt(errorCode, 10);
    if (files.length === 1) {
      return this.getSingularErrorMessageForDownloadFinishedEvent(errorCode, files[0].to);
    }

    return this.getPluralErrorMessageForDownloadFinishedEvent(errorCode, files.length);
  }

  getSingularErrorMessageForDownloadFinishedEvent(errorCode, path) {
    switch (errorCode) {
      case DownloadFinishedErrorCodes.FILE_ALREADY_EXISTS:
        return translateFn(
          '{NAME_OF_FILE} could not be uploaded because a file with the same name already exists in the specified path. Please rename the file.',
          {
            NAME_OF_FILE: getFilenameFromPath(path),
          }
        );
      case DownloadFinishedErrorCodes.NOT_PROCESSED:
        return translateFn(
          '{NAME_OF_FILE} could not be uploaded because the task was canceled or it timed out, the file exceeded the size limit, or there was a processing error.',
          {
            NAME_OF_FILE: getFilenameFromPath(path),
          }
        );
      case DownloadFinishedErrorCodes.OPERATION_CANCELLED:
        return translateFn(
          '{NAME_OF_FILE} could not be uploaded because the file path contains unsupported characters.',
          {
            NAME_OF_FILE: getFilenameFromPath(path),
          }
        );
      case DownloadFinishedErrorCodes.RELATIVE_PATH:
        return translateFn(
          '{NAME_OF_FILE} could not be uploaded because a relative file path was specified. Provide the full file path.',
          {
            NAME_OF_FILE: getFilenameFromPath(path),
          }
        );
      case DownloadFinishedErrorCodes.ACCESS_DENIED_BEST_DIR:
      case DownloadFinishedErrorCodes.ACCESS_DENIED:
        return translateFn(
          '{NAME_OF_FILE} could not be uploaded due to insufficient permissions. Choose a different folder.',
          {
            NAME_OF_FILE: getFilenameFromPath(path),
          }
        );
      case DownloadFinishedErrorCodes.INVALID_PATH:
        return translateFn('{NAME_OF_FILE} could not be uploaded because the file path is not valid.', {
          NAME_OF_FILE: getFilenameFromPath(path),
        });
      default:
        return translateFn(
          '{NAME_OF_FILE} could not be uploaded. Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support',
          {
            NAME_OF_FILE: getFilenameFromPath(path),
            ERROR_CODE: errorCode,
          }
        );
    }
  }

  getPluralErrorMessageForDownloadFinishedEvent(errorCode, numberOfFiles) {
    switch (errorCode) {
      case DownloadFinishedErrorCodes.FILE_ALREADY_EXISTS:
        return translateFn(
          '{NUMBER_OF_FILES} files could not be uploaded because files with the same name already exist in the specified path. Please rename the files.',
          {
            NUMBER_OF_FILES: numberOfFiles,
          }
        );
      case DownloadFinishedErrorCodes.NOT_PROCESSED:
        return translateFn(
          '{NUMBER_OF_FILES} files could not be uploaded because the task was canceled or it timed out, the files exceeded the size limit, or there was a processing error.',
          {
            NUMBER_OF_FILES: numberOfFiles,
          }
        );
      case DownloadFinishedErrorCodes.OPERATION_CANCELLED:
        return translateFn(
          '{NUMBER_OF_FILES} files could not be uploaded because the file path contains unsupported characters.',
          {
            NUMBER_OF_FILES: numberOfFiles,
          }
        );
      case DownloadFinishedErrorCodes.RELATIVE_PATH:
        return translateFn(
          '{NUMBER_OF_FILES} files could not be uploaded because a relative file path was specified. Provide the full file path.',
          {
            NUMBER_OF_FILES: numberOfFiles,
          }
        );
      case DownloadFinishedErrorCodes.ACCESS_DENIED_BEST_DIR:
      case DownloadFinishedErrorCodes.ACCESS_DENIED:
        return translateFn(
          '{NUMBER_OF_FILES} files could not be uploaded due to insufficient permissions. Choose a different folder.',
          {
            NUMBER_OF_FILES: numberOfFiles,
          }
        );
      case DownloadFinishedErrorCodes.INVALID_PATH:
        return translateFn('{NUMBER_OF_FILES} files could not be uploaded because the file path is not valid.', {
          NUMBER_OF_FILES: numberOfFiles,
        });
      default:
        return translateFn(
          '{NUMBER_OF_FILES} files could not be uploaded. Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
          {
            NUMBER_OF_FILES: numberOfFiles,
            ERROR_CODE: errorCode,
          }
        );
    }
  }
}
